'use client';

import { useMemo } from 'react';
import { Star } from 'lucide-react';
import { Card } from './card.js';
import { SimpleVideoPlayer } from './simple-video-player.js';

export interface Testimonial {
  id: string;
  name: string;
  company: string;
  quote: string;
  content?: string;
  rating: number;
}

export interface VideoTestimonial extends Testimonial {
  /** Primary field used by SimpleVideoPlayer */
  videoSrc?: string;
  /** Alias used by @west-tennessee-consulting/types website data; resolved when videoSrc is absent */
  videoUrl?: string;
  thumbnail: string;
  duration: string;
}

interface VideoTestimonialCardProps {
  testimonial: VideoTestimonial;
}

const VideoTestimonialCard = ({
  testimonial
}: VideoTestimonialCardProps) => {
  const resolvedVideoSrc =
    testimonial.videoSrc ?? testimonial.videoUrl ?? '';

  return (
    <div className="flex flex-col space-y-4 p-6 bg-background border border-border rounded-xl transform transition-all duration-300 hover:shadow-lg">
      <div className="relative aspect-[9/16] w-full max-w-[280px] mx-auto">
        <SimpleVideoPlayer
          videoSrc={resolvedVideoSrc}
          thumbnail={testimonial.thumbnail}
          className="w-full h-full"
          muted={false}
          useNativeControls={true}
          label={`Video testimonial from ${testimonial.name}${
            testimonial.company ? `, ${testimonial.company}` : ''
          }`}
        />
      </div>

      <div className="text-center space-y-3">
        <div className="flex items-center justify-center gap-1">
          {Array.from({ length: testimonial.rating || 5 }).map((_, i) => (
            <Star key={i} className="w-4 h-4 fill-primary text-primary" />
          ))}
        </div>

        {testimonial.quote && (
          <blockquote className="text-sm text-muted-foreground italic leading-relaxed">
            "{testimonial.quote}"
          </blockquote>
        )}

        <div className="space-y-1">
          <div className="font-semibold text-foreground">{testimonial.name}</div>
          <div className="text-sm text-muted-foreground">{testimonial.company}</div>
        </div>
      </div>
    </div>
  );
};

interface WrittenTestimonialCardProps {
  testimonial: Testimonial;
}

const WrittenTestimonialCard = ({ testimonial }: WrittenTestimonialCardProps) => {
  return (
    <Card className="p-6 flex flex-col space-y-4">
      <div className="flex items-center gap-1">
        {Array.from({ length: testimonial.rating || 5 }).map((_, i) => (
          <Star key={i} className="w-4 h-4 fill-primary text-primary" />
        ))}
      </div>

      <blockquote className="text-sm text-muted-foreground italic leading-relaxed flex-grow">
        "{testimonial.quote}"
      </blockquote>

      <div className="space-y-1 pt-4 border-t border-border">
        <div className="font-semibold text-foreground">{testimonial.name}</div>
        <div className="text-sm text-muted-foreground">{testimonial.company}</div>
      </div>
    </Card>
  );
};

interface TestimonialsProps {
  videoTestimonials: VideoTestimonial[];
  writtenTestimonials: Testimonial[];
  title?: string;
  subtitle?: string;
}

/**
 * Reusable Testimonials component with video and written testimonials.
 * Uses SimpleVideoPlayer for robust video playback across all apps.
 * 
 * Usage:
 * ```tsx
 * import { Testimonials } from '@west-tennessee-consulting/ui';
 * import { videoTestimonials, testimonials } from '@/lib/testimonials';
 * 
 * export function TestimonialsSection() {
 *   return (
 *     <Testimonials
 *       videoTestimonials={videoTestimonials}
 *       writtenTestimonials={testimonials}
 *       title="What Our Customers Say"
 *       subtitle="Join thousands of satisfied customers"
 *     />
 *   );
 * }
 * ```
 */
export function Testimonials({
  videoTestimonials,
  writtenTestimonials,
  title = 'What Our Customers Say',
  subtitle = 'Join thousands of satisfied customers'
}: TestimonialsProps) {
  const memoizedVideoTestimonials = useMemo(() => {
    return videoTestimonials.map((testimonial) => (
      <VideoTestimonialCard
        key={testimonial.id}
        testimonial={testimonial}
      />
    ));
  }, [videoTestimonials]);

  const memoizedWrittenTestimonials = useMemo(() => {
    return writtenTestimonials.map((testimonial) => (
      <WrittenTestimonialCard key={testimonial.id} testimonial={testimonial} />
    ));
  }, [writtenTestimonials]);

  return (
    <section className="w-full py-12 md:py-24 lg:py-32 bg-muted/30">
      <div className="container mx-auto px-4 md:px-6">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold tracking-tighter sm:text-4xl md:text-5xl mb-4">
            {title}
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">{subtitle}</p>
        </div>

        {/* Video Testimonials */}
        {videoTestimonials.length > 0 && (
          <div className="mb-16">
            <h3 className="text-xl font-semibold text-center mb-8">Video Testimonials</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              {memoizedVideoTestimonials}
            </div>
          </div>
        )}

        {/* Written Testimonials */}
        {writtenTestimonials.length > 0 && (
          <div>
            <h3 className="text-xl font-semibold text-center mb-8">Customer Stories</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {memoizedWrittenTestimonials}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

